import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS, CONTACT } from "@/lib/pnp";
import { RevealLines } from "./Reveal";
import { ArrowDown, Phone } from "lucide-react";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-white pt-24 md:pt-28"
    >
      {/* Overline */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        className="px-6 md:px-12 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-[#17C964] inline-block" />
          <span className="font-mono-pp text-[11px] uppercase tracking-[0.22em] text-[#4F6858]">
            P&amp;P — Est. Grünberg
          </span>
        </div>
        <span className="hidden md:block font-mono-pp text-[11px] uppercase tracking-[0.22em] text-[#4F6858]">
          {CONTACT.serviceArea}
        </span>
      </motion.div>

      {/* Main grid */}
      <div className="relative px-6 md:px-12 mt-10 md:mt-14 grid grid-cols-12 gap-6 md:gap-10 items-start">
        {/* Text block */}
        <motion.div
          style={{ y: textY, opacity }}
          className="col-span-12 lg:col-span-8"
        >
          <h1
            data-testid="hero-heading"
            className="font-display font-extrabold text-[#082E19] leading-[0.88] tracking-tighter"
            style={{ fontSize: "clamp(3rem, 9.2vw, 8.5rem)" }}
          >
            <RevealLines
              lines={[
                "Ich bringe",
                <span key="l2" className="italic font-medium">
                  Ihren Garten
                </span>,
                <span key="l3">
                  in <span className="text-[#17C964]">Form.</span>
                </span>,
              ]}
              delay={0.25}
              stagger={0.08}
            />
          </h1>

          <div className="mt-10 md:mt-14 grid grid-cols-12 gap-6">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 md:col-span-6 text-[15px] md:text-base text-[#4F6858] max-w-md text-balance leading-relaxed"
            >
              Zuverlässig. Sauber. Fair. Ein junger Gärtner aus Grünberg, der
              Ihren Garten so behandelt, als wäre es der eigene.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 md:col-span-6 flex flex-wrap items-center gap-3"
            >
              <a
                data-testid="hero-cta-call"
                href={CONTACT.telHref}
                className="pp-btn pp-btn-primary"
              >
                <Phone size={16} strokeWidth={2} />
                {CONTACT.phone}
              </a>
              <a
                data-testid="hero-cta-quote"
                href="#kontakt"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="pp-btn pp-btn-ghost"
              >
                Kostenloses Angebot →
              </a>
            </motion.div>
          </div>
        </motion.div>

        {/* Parallax image */}
        <div className="col-span-12 lg:col-span-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
            className="relative w-full aspect-[3/4] overflow-hidden rounded-md ring-1 ring-[#DFEBE4]"
          >
            <motion.img
              src={ASSETS.hedgeAlley}
              alt="Hecken-Allee gepflegt von P&P Gartenservice"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 h-[115%] w-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
            <div className="absolute left-4 bottom-4 right-4 flex items-end justify-between text-white">
              <div>
                <div className="font-mono-pp text-[10px] uppercase tracking-[0.22em] opacity-80">Projekt · 042</div>
                <div className="font-display font-semibold text-lg leading-tight mt-1">Kirschlorbeer-Allee</div>
              </div>
              <span className="font-mono-pp text-[10px] uppercase tracking-[0.22em] opacity-80">Grünberg</span>
            </div>
          </motion.div>

          {/* Floating chip */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="hidden md:flex absolute -left-6 top-8 items-center gap-2 bg-white ring-1 ring-[#DFEBE4] rounded-full px-4 py-2 shadow-sm"
          >
            <span className="h-2 w-2 rounded-full bg-[#17C964] animate-pulse" />
            <span className="font-mono-pp text-[10px] uppercase tracking-[0.22em] text-[#082E19]">
              Jetzt buchbar
            </span>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="font-mono-pp text-[10px] uppercase tracking-[0.24em] text-[#4F6858]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="text-[#082E19]"
        >
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
}
